import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { Button, LoadingSkeleton, LoadingSkeletonProvider } from "capitalrx-components";
import { formatDate } from "capitalrx-utilities";

import { downloadBatchTestClaimActions } from "@/App/actions";
import { getBatchTestAPI } from "@/App/api/batch_test/get";
import { useAPIGetQuery } from "@/App/api-hooks/get";
import SectionContainer from "@/App/components/SectionContainer";
import { defaultDateFormat } from "@/App/constants/constants";
import { BATCH_TEST_URLS } from "@/App/constants/urls";

interface Props {
  fileID: string;
}

const FileDownload: React.FC<Props> = ({ fileID }: Props) => {
  const dispatch = useDispatch();
  const { downloadTestClaim } = downloadBatchTestClaimActions;
  const [options, setOptions] = useState({
    // refetch every 10 seconds by default
    refetchInterval: 10000,
  });

  const batchTestClaim = useAPIGetQuery(BATCH_TEST_URLS.GET, getBatchTestAPI, fileID, options);

  const status = batchTestClaim?.data?.status;
  const loading = status === "";

  // stop refreshing if the file is processed
  if (batchTestClaim?.data?.status === "processed" && options !== undefined) {
    setOptions(undefined);
  }

  const handleButtonClick = (): void => {
    dispatch(downloadTestClaim(fileID));
  };

  const handleLogsButtonClick = (): void => {
    dispatch(downloadTestClaim(fileID, "logs"));
  };

  const downloadButtonContent = (
    <Button buttonStyle="solid" color="darkblue" handleClick={handleButtonClick}>
      <span className="caprx-icon-download" /> Download
    </Button>
  );

  const logButtonContent = batchTestClaim?.data?.log_key ? (
    <Button buttonStyle="solid" color="darkblue" handleClick={handleLogsButtonClick}>
      <span className="caprx-icon-download" /> Download Logs
    </Button>
  ) : null;

  const original_name = batchTestClaim?.data?.original_name;
  let header = `File ${batchTestClaim?.data?.original_name} is being processed. We will send an email once this file is completed.`;
  if (status === "processed") {
    header = `File ${original_name} processed.`;
  }

  return (
    <LoadingSkeletonProvider loading={loading}>
      <SectionContainer theme="read-only-form-page-wrapper" headerTitle="Output" sectionTitleWithBtn={false}>
        <LoadingSkeleton>
          <div className="download-test-batch-claim-wrapper">
            <div className="download-test-batch-claim-header">{header}</div>
            <div>{`Uploaded ${formatDate(batchTestClaim?.data?.created_at, defaultDateFormat)}`}</div>
            {status === "processed" && (
              <div className="mt25">
                {downloadButtonContent}&nbsp;{logButtonContent}
              </div>
            )}
          </div>
        </LoadingSkeleton>
      </SectionContainer>
    </LoadingSkeletonProvider>
  );
};

export default FileDownload;
